import { css, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import './d-custom-slide';
import './d-news-slide-nrk';
import './d-news-slide-fhi';
import './d-employees-slide';
import './d-weather-slide';
import 'src/library/fields/d-closer';
import type {
  CustomSlide,
  EmployeesSlide,
  FhiSlide,
  NewsSlide,
  RssNewsItem,
  Slide,
  SlideElement,
  SlideSuggestionCategory,
  WeatherSlide,
} from 'src/content/screens/slide-element';
import { plainTextFromMaybeHtml, uuid } from 'src/utilities/text';
import { isUserSlideMatchingSuggestion } from 'src/content/screens/slide-equality';
import type { LibraryItem } from 'src/library/screen/library-item';
import 'src/library/elements/d-popover';
import { ResponsiveElement } from 'src/library/elements/responsive-container';
import type { DPopover } from 'src/library/elements/d-popover';
import type { EmployeePresenceStatus } from 'src/store/selectors/staffing/employee-presence-status';
import { resolveAssetUrl } from './resolve-asset-url';
import { AlertDialog } from 'src/library/components/alert-dialog';
import type { AlertDialogResult } from 'src/library/components/alert-dialog-result';
import { slideDeleteButtonStyle } from 'src/content/screens/slide-delete-button-style';
import { t } from 'src/i18n';

const USER_SLIDES_CATEGORY_ID = 'user-slides';

/**
 *
 */
@customElement('d-edit-slide')
export class DEditSlide extends ResponsiveElement {
  static readonly styles = [
    slideDeleteButtonStyle,
    css`
      /* Layout styles */
      :host {
        --min-margin-top: 20px;
        --min-margin-bottom: 30px;
        --margin-left: 20px;
        --margin-right: 20px;
        --height: calc(100vh - var(--min-margin-top) - var(--min-margin-bottom));
        --width: calc(100vw - var(--margin-left) - var(--margin-right));
        --tool-area-height: 312px;
        --padding-top-for-vertical-center: calc(
          (var(--height) - (var(--width) * 9 / 16 + var(--tool-area-height))) / 2
        );
        display: block;
      }
      .wrapper {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        width: var(--width);
        height: var(--height);
        margin-left: var(--margin-left);
        margin-top: var(--min-margin-top);
        padding-top: var(--padding-top-for-vertical-center);
      }
      .wrapper.mobile {
        opacity: 0;
      }
      .liner {
        flex: 1;
        /* Needed to make content shrink */
        min-height: 0;
      }
      .slide {
        display: flex;
        align-items: center;
        justify-content: center;
        aspect-ratio: 16/9;
        max-height: 100%;
        width: var(--width);
        max-width: calc(((var(--height)) - var(--tool-area-height)) * 16 / 9);
        margin: 0 auto;
      }
      .tools {
        position: relative;
        box-sizing: border-box;
        flex: 0;
        height: var(--tool-area-height);
        max-width: calc(((var(--height)) - var(--tool-area-height)) * 16 / 9);
        margin: 0 auto;
        z-index: 1;
      }
      /* Content styles */
      .slide.main > * {
        width: 100%;
        aspect-ratio: 16/9;
        border: 0.1cqw solid hsla(0, 0%, 80%, 0.5);
        border-radius: 1.4cqi;
        overflow: hidden;
      }
      .tools-row {
        display: flex;
        gap: 30px;
        margin-top: 20px;
      }
      .suggestions-section {
        flex: 1;
      }
      .tool-label {
        display: flex;
        align-items: baseline;
        gap: 8px;
        margin-bottom: 10px;
        color: white;
        font-size: 17px;
      }
      .label-button {
        font-size: 15px;
        color: var(--themeColorLighterOne);
        cursor: pointer;
      }
      @media (hover: hover) {
        .label-button:hover {
          color: white;
        }
      }
      .tools-row-content.mini {
        align-items: center;
      }
      .tool-mini-button {
        margin-left: -6px;
        padding: 6px;
        font-size: 17px;
        color: var(--themeColor);
        cursor: pointer;
      }
      @media (hover: hover) {
        .tool-mini-button:hover {
          color: white;
        }
      }
      .split {
        display: flex;
        justify-content: space-between;
      }
      .split > div:first-child {
        flex: 1;
      }
      .split > div:last-child {
        width: 200px;
        flex: none;
      }
      .tools-row-content {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
      }
      #suggestions.tools-row-content {
        gap: 20px;
      }
      .thumbnail {
        width: 120px;
        border: 1px solid hsla(0, 0%, 80%, 0.5);
        border-radius: 6px;
        overflow: hidden;
        cursor: pointer;
        z-index: 1;
      }
      .suggestion {
        position: relative;
        width: 140px;
      }
      .large .thumbnail {
        width: 140px;
      }
      .suggestion.extra-large,
      .suggestion.extra-large .thumbnail {
        width: 300px;
      }
      .suggestion.extra-large .thumbnail {
        border: 2px solid hsla(0, 0%, 80%, 1);
        border-radius: 16px;
      }
      .suggestion img {
        display: block;
        width: 100%;
        aspect-ratio: 16/9;
      }
      .thumbnail d-custom-slide {
        overflow: hidden;
      }
      .thumbnail.selected {
        border-color: black;
      }
      .slide-name {
        margin-top: 10px;
        text-align: center;
        font-size: 14px;
        font-weight: 200;
        color: white;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        overflow: hidden;
      }
      .popover-suggestions .slide-name {
        color: black;
        font-weight: normal;
      }
      .suggestion-cover {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        cursor: pointer;
      }
      .actions {
        height: 78px;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        margin-top: 48px;
      }
      .mini .actions {
        margin-top: 0;
      }
      .layout-button {
        position: relative;
        display: flex;
        flex-wrap: wrap;
        gap: 5px;
        width: 140px;
        aspect-ratio: 16/9;
      }
      .layout-button .thumbnail {
        width: 65px;
        border: none;
        border-radius: 4px;
      }
      .layout-button .cover {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        cursor: pointer;
        z-index: 1;
      }
      .popover {
        box-sizing: border-box;
        max-width: calc(100vw - 40px);
        max-height: calc(100vh - 100px);
        border-radius: 8px;
        padding: 10px;
        background: white;
        box-shadow: 0 2px 10px hsla(1, 0%, 0%, 0.3);
        overflow-y: auto;
      }
      .popover-templates {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        overflow: auto;
        width: 270px;
      }
      .popover-suggestions {
        width: 1320px;
        border-radius: 28px;
        padding: 0 30px 30px;
      }
      .popover-suggestions d-closer {
        top: 10px;
        right: 10px;
        width: 30px;
        height: 30px;
        z-index: 1;
      }
      .popover-suggestions .category {
        border-bottom: 1px solid var(--borderColor);
        padding-bottom: 12px;
      }
      .popover-suggestions .category:first-child {
        padding-top: 0;
      }
      .popover-suggestions .category:last-child {
        border-bottom: none;
        padding-bottom: 0;
      }
      .popover-suggestions .category .name {
        position: sticky;
        top: 0;
        margin-right: -4px;
        padding-top: 20px;
        padding-bottom: 4px;
        background: white;
        font-size: 24px;
        font-weight: 500;
        z-index: 1;
      }
      .popover-suggestions .category .description {
        margin-bottom: 10px;
        font-size: 16px;
        font-weight: normal;
      }
      .popover-suggestions .category .description a {
        color: var(--themeColor);
        text-decoration: none;
      }
      @media (hover: hover) {
        .popover-suggestions .category .description a:hover {
          color: black;
        }
      }
      .popover-suggestions .slides {
        display: flex;
        flex-wrap: wrap;
        gap: 12px 20px;
      }
      .suggestion .delete {
        right: 2px;
      }
      @media (max-width: 1340px) {
        .popover-suggestions {
          width: 1000px;
        }
      }
      @media (max-width: 1040px) {
        .popover-suggestions {
          width: 664px;
          padding-left: 20px;
          padding-right: 20px;
        }
      }
      @media (max-width: 700px) {
        .popover-suggestions {
          width: 344px;
        }
      }
      @media (max-width: 990px), (max-height: 900px) {
        :host {
          --tool-area-height: 188px;
        }
      }
      @media (max-width: 900px), (max-height: 820px) {
        :host {
          --tool-area-height: 100px;
        }
      }
    `,
  ];
  @property({ type: Object })
  slide!: Slide;
  @property({ type: Array })
  templates: CustomSlide[] = [];
  @property({ type: Array })
  newsFeed: RssNewsItem[] = [];
  @property({ type: Array })
  newsFeedFhi: RssNewsItem[] = [];
  @property({ type: Array })
  employeesPresenceStatus: EmployeePresenceStatus[] = [];
  @property({ type: Array })
  suggestionsSelected: Slide[] = [];
  @property({ type: Array })
  suggestionsCategorized: SlideSuggestionCategory[] = [];
  @property({ type: Array })
  media: string[] = [];
  @property({ type: Array })
  library: LibraryItem[] = [];
  @property({ type: String })
  selectedMedia = '';
  @property({ type: String })
  assetBase = '';
  @property({ type: Boolean })
  newSlide = false;
  @property({ type: Object })
  usedSavedSlideIds: Set<string> = new Set();
  @state()
  private uiSize: 'mini' | 'midi' | 'maxi' | 'mobile' = 'midi';
  @state()
  private suggestionsCount = 0;
  @query('#popover-suggestions')
  private popoverSuggestions!: DPopover;

  private setUiSize() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    if (width > 990 && height > 900) {
      this.uiSize = 'maxi';
    } else if (width > 900 && height > 820) {
      this.uiSize = 'midi';
    } else if (width > 560) {
      this.uiSize = 'mini';
    } else {
      this.uiSize = 'mobile';
    }
  }

  private setSuggestionsCount() {
    const container = this.shadowRoot?.getElementById('suggestions');
    const item = this.shadowRoot?.getElementById('suggestion');
    if (container && item) {
      const containerStyles = getComputedStyle(container);
      const gap = Number(containerStyles.gap.replace('px', ''));
      const containerWidth = container.getBoundingClientRect().width;
      const itemWidth = item.getBoundingClientRect().width + gap;
      const itemsCount = Math.floor(containerWidth / itemWidth);
      if (itemsCount !== this.suggestionsCount) {
        this.suggestionsCount = itemsCount;
      }
    }
  }

  onResize() {
    this.setUiSize();
    this.setSuggestionsCount();
  }

  private get templatesWithSelectedMedia(): CustomSlide[] {
    if (!this.selectedMedia) {
      return this.templates;
    }
    return this.templates.map((t) => {
      return { ...t, media: this.selectedMedia };
    });
  }

  /**
   * Lagrede custom-slides fra library — server persisterer dem automatisk
   * ved playlist-save (trinnvis/dabih-api#4421). slide.id settes til
   * library-id-en (`slide-{hash}`) så `onDeleteSavedSlide` kan kalle
   * DELETE /signage/library/{itemId} direkte uten ekstra mapping.
   * Når brukeren bruker forslaget i en playlist får sliden uansett ny id i
   * `onSelectSuggestion` (basert på slot-id-en), så library-id-en lekker
   * ikke videre inn i payloaden.
   *
   * Tre normaliseringer fordi server-payloaden ikke matcher klient-typen
   * helt (per testing 2026-04-30):
   *  - `slide.type` kommer som null — overstyrer til 'custom' siden
   *    `mediaType === 'slide'` allerede har gjort den samme sortering.
   *  - `slide.assets` kommer som null — fall tilbake til [media] så
   *    rendring og asset-bruk-sjekken har noe å henge seg på.
   *  - `name`/`duration` overstyres fra outer library-item som er kanonisk.
   *
   * Filtrerer bort items som er strukturelt identiske med standard-forslag
   * (#12768) — server lagrer alle playlist-slides som library-items, men en
   * uendret mal er ikke et "egen" lysbilde og bør ikke dukke opp i Mine
   * lysbilder-kategorien.
   */
  private get userSlides(): Slide[] {
    return this.library
      .filter((item) => item.signageMediaType === 'slide' && item.slide)
      .map((item) => {
        const inner = item.slide as CustomSlide;
        // Navnet avledes fra et rik tekst-felt (overskriften). Rydd bort
        // eventuell legacy-markup og fall tilbake til et standardnavn når
        // resultatet er tomt, slik at vi aldri viser bokstavelige HTML-tags
        // (f.eks. `<p></p>`) eller et blankt navn i «Mine lysbilder».
        const name =
          plainTextFromMaybeHtml(item.name) || plainTextFromMaybeHtml(inner.name) || t('screens.editSlide.savedSlide');
        return {
          ...inner,
          id: item.id,
          name,
          duration: item.defaultDuration || inner.duration || 10,
        };
      })
      .filter((slide) => !isUserSlideMatchingSuggestion(slide));
  }

  private addId(item: SlideElement): SlideElement {
    item = { ...item, id: uuid() };
    if (item.type === 'divider' && item.first && item.last) {
      item.first = this.addId(item.first);
      item.last = this.addId(item.last);
    }
    if (item.overlayElement) {
      item.overlayElement = this.addId(item.overlayElement);
    }
    return item;
  }

  private onSelectTemplate(template: CustomSlide) {
    const popover = this.shadowRoot?.getElementById('popover-templates') as DPopover;
    if (popover) {
      popover.close();
    }
    if (this.slide?.type === 'custom') {
      this.slide = {
        ...this.slide,
        layout: this.addId(template.layout),
      };
    } else {
      const id = this.slide?.id ?? uuid();
      this.slide = {
        ...template,
        id,
        layout: this.addId(template.layout),
      };
    }
    this.fireChange();
  }

  private onSelectSuggestion(slide: Slide) {
    const popover = this.shadowRoot?.getElementById('popover-suggestions') as DPopover;
    if (popover) {
      popover.close();
    }
    const id = this.slide?.id ?? uuid();
    if (slide.type === 'custom') {
      this.slide = {
        ...slide,
        id,
        layout: this.addId(slide.layout),
      };
    } else {
      this.slide = { ...slide, id };
    }
    this.fireChange();
  }

  private customSlideWithIds(slide: CustomSlide): CustomSlide {
    return { ...slide, layout: this.addId(slide.layout) };
  }

  private onCustomSlideChanged(e: CustomEvent<{ value: CustomSlide }>) {
    e.stopPropagation();
    this.slide = { ...e.detail.value, layout: this.removeId(e.detail.value.layout) };
    this.fireChange();
  }

  private onNewsSlideChanged(e: CustomEvent<{ value: NewsSlide | FhiSlide }>) {
    e.stopPropagation();
    this.slide = { ...e.detail.value };
    this.fireChange();
  }

  private onWeatherSlideChanged(e: CustomEvent<{ value: WeatherSlide }>) {
    e.stopPropagation();
    this.slide = { ...e.detail.value };
    this.fireChange();
  }

  private onEmployeesSlideChanged(e: CustomEvent<{ value: EmployeesSlide }>) {
    e.stopPropagation();
    this.slide = { ...e.detail.value };
    this.fireChange();
  }

  private fireChange() {
    this.dispatchEvent(
      new CustomEvent<{ value: Slide }>('value-changed', {
        bubbles: true,
        composed: true,
        detail: { value: this.slide },
      }),
    );
  }

  private onSelectMedia(e: CustomEvent<{ src: string }>) {
    this.selectedMedia = e.detail.src;
  }

  private removeId(editItem: SlideElement): SlideElement {
    const item = { ...editItem };
    delete item.id;
    if (item.type === 'divider' && item.first && item.last) {
      item.first = this.removeId(item.first);
      item.last = this.removeId(item.last);
    }
    if (item.overlayElement) {
      item.overlayElement = this.removeId(item.overlayElement);
    }
    return item;
  }

  private get moreSuggestions() {
    return this.suggestionsSelected.slice(1, this.suggestionsCount);
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    this.setUiSize();
    this.setSuggestionsCount();
  }

  private renderTemplatesButton() {
    const templates = [...this.templatesWithSelectedMedia];
    templates.length = 4;
    return html`
      <d-popover id="popover-templates" horizontal="right" vertical="up" .cover=${true}>
        <div slot="content" class="popover popover-templates">
          ${this.templatesWithSelectedMedia.map((t) => {
            return html`
              <div class="thumbnail" @click=${() => this.onSelectTemplate(t)}>
                <d-custom-slide thumbnail .slide=${t} .assetBase=${this.assetBase}></d-custom-slide>
              </div>
            `;
          })}
        </div>
        <div slot="trigger" class="layout-button">
          ${templates.map((t) => {
            return html`
              <div class="thumbnail">
                <d-custom-slide thumbnail .slide=${t} .assetBase=${this.assetBase}></d-custom-slide>
              </div>
            `;
          })}
          <div class="cover"></div>
        </div>
      </d-popover>
    `;
  }

  private renderSuggestionThumbnail(slide: Slide) {
    if (slide.type === 'custom') {
      return html` <d-custom-slide .slide=${slide} .assetBase=${this.assetBase}></d-custom-slide> `;
    }
    if (slide.type === 'fhi') {
      return html` <d-news-slide-fhi .slide=${slide} .feed=${this.newsFeedFhi}></d-news-slide-fhi>`;
    }
    if (slide.type === 'news') {
      return html` <d-news-slide-nrk .slide=${slide} .feed=${this.newsFeed}></d-news-slide-nrk>`;
    }
    if (slide.type === 'weather') {
      return html`<d-weather-slide .slide=${slide} .assetBase=${this.assetBase}></d-weather-slide> `;
    }
    if (slide.type === 'employees') {
      return html`<d-employees-slide .slide=${slide} .employees=${this.employeesPresenceStatus}></d-employees-slide>`;
    }
    if (slide.type === 'image') {
      return html`<img src="${resolveAssetUrl(slide.media, this.assetBase)}" />`;
    }
    return nothing;
  }

  private renderSuggestion(slide: Slide, size: string, id: string = '', deletable = false, inUse = false) {
    return html`
      <div class="suggestion ${size}" id="${id}">
        <div class="thumbnail">${this.renderSuggestionThumbnail(slide)}</div>
        <div class="slide-name">${slide.name}</div>
        ${deletable
          ? html`<button
              class="delete"
              aria-label="${t('screens.editSlide.deleteSlideAriaLabel', { name: slide.name })}"
              @click=${(e: MouseEvent) => this.onDeleteSavedSlide(e, slide, inUse)}
            ></button>`
          : nothing}
        <div class="suggestion-cover" @click=${() => this.onSelectSuggestion(slide)}></div>
      </div>
    `;
  }

  private renderSuggestionsPopover() {
    return html`
      <d-popover manual id="popover-suggestions" background="white" border-radius="28">
        <div slot="content" class="popover popover-suggestions">
          ${this.suggestionsCategorized.map((category) => {
            // user-slides-kategorien overstyres med library-deriverte items
            // så listen alltid speiler nyeste server-state.
            const slides = category.id === USER_SLIDES_CATEGORY_ID ? this.userSlides : category.slides;
            const isUserCategory = category.id === USER_SLIDES_CATEGORY_ID;
            return html`
              <div class="category">
                <div class="name">${category.name}</div>
                <div class="description">
                  ${category.description}
                  ${category.descriptionLink
                    ? html` <a target="_blank" href=${category.descriptionLink.href}
                        >${category.descriptionLink.text}</a
                      >`
                    : nothing}
                </div>
                <div class="slides">
                  ${slides.map((slide) => {
                    // Krysset vises alltid på egne slides — er sliden i bruk forklarer
                    // dialogen hvorfor den ikke kan slettes (kryss som forsvinner uten
                    // forklaring forvirret brukere, se #13323).
                    const inUse = this.usedSavedSlideIds.has(slide.id);
                    return html`${this.renderSuggestion(slide, 'extra-large', '', isUserCategory, inUse)}`;
                  })}
                </div>
              </div>
            `;
          })}
          <d-closer @click=${() => this.popoverSuggestions.close()}></d-closer>
        </div>
      </d-popover>
    `;
  }

  private async onDeleteSavedSlide(e: MouseEvent, slide: Slide, inUse = false) {
    e.stopPropagation();
    if (inUse) {
      await AlertDialog.open({
        title: t('screens.editSlide.deleteSlideTitle'),
        content: t('screens.editSlideDialog.slideInUse'),
        okDisabled: true,
      });
      return;
    }
    const result: AlertDialogResult = await AlertDialog.open({
      title: t('screens.editSlide.deleteSlideTitle'),
      content: t('screens.editSlide.deleteSlideContent'),
    });
    if (result.action !== 'ok') return;
    this.dispatchEvent(
      new CustomEvent<{ id: string }>('saved-slide-delete', {
        bubbles: true,
        composed: true,
        detail: { id: slide.id },
      }),
    );
  }

  private renderSuggestions() {
    return html`
      <div class="tool-section suggestions-section">
        <div class="tool-label">
          ${t('screens.editSlide.contentSuggestions')}
          <span class="label-button" @click=${(e) => this.popoverSuggestions.open(e)}
            >${t('screens.editSlide.seeAllSuggestions')}</span
          >
        </div>
        <div class="tools-row-content" id="suggestions">
          ${this.renderSuggestion(this.suggestionsSelected[0], 'large', 'suggestion')}
          ${this.moreSuggestions.map((slide) => {
            return html`${this.renderSuggestion(slide, 'large')}`;
          })}
        </div>
      </div>
    `;
  }

  private renderToolsMaxi() {
    return html` <div class="tools-row">${this.renderSuggestions()}</div> `;
  }

  private renderToolsMidi() {
    return html`
      <div class="tools-row">
        <div class="tool-section">
          <div class="tool-label">${t('screens.editSlide.chooseLayout')}</div>
          <div class="tools-row-content">${this.renderTemplatesButton()}</div>
        </div>
        ${this.renderSuggestions()}
      </div>
    `;
  }

  private renderToolsMini() {
    return html`
      <div class="tools-row">
        <div class="tool-section">
          <div class="tools-row-content">
            <d-popover id="popover-templates" horizontal="right" vertical="up" .cover=${true}>
              <div slot="content" class="popover popover-templates">
                ${this.templatesWithSelectedMedia.map((t) => {
                  return html`
                    <div class="thumbnail" @click=${() => this.onSelectTemplate(t)}>
                      <d-custom-slide thumbnail .slide=${t} .assetBase=${this.assetBase}></d-custom-slide>
                    </div>
                  `;
                })}
              </div>
              <div slot="trigger" class="tool-mini-button">${t('screens.editSlide.chooseLayout')}</div>
            </d-popover>
            <span class="tool-mini-button" @click=${(e) => this.popoverSuggestions.open(e)}
              >${t('screens.editSlide.seeContentSuggestions')}</span
            >
          </div>
        </div>
      </div>
    `;
  }

  private renderTools() {
    if (this.uiSize === 'maxi') {
      return html`<div class="tools-maxi">${this.renderToolsMaxi()}</div>`;
    }
    if (this.uiSize === 'midi') {
      return html`<div class="tools-midi">${this.renderToolsMidi()}</div>`;
    }
    return html`<div class="tools-mini">${this.renderToolsMini()}</div>`;
  }

  renderSlideType(slide: Slide) {
    if (slide.type === 'custom') {
      return html`
        <d-custom-slide
          .edit=${true}
          .slide=${this.customSlideWithIds(slide)}
          .media=${this.media}
          .library=${this.library}
          .assetBase=${this.assetBase}
          @value-changed=${(e: CustomEvent<{ value: CustomSlide }>) => this.onCustomSlideChanged(e)}
          @select-media=${(e: CustomEvent<{ src: string }>) => this.onSelectMedia(e)}
        ></d-custom-slide>
      `;
    }
    if (slide.type === 'fhi') {
      return html` <d-news-slide-fhi
        edit
        .slide=${slide}
        .feed=${this.newsFeedFhi}
        @value-changed=${(e: CustomEvent<{ value: FhiSlide }>) => this.onNewsSlideChanged(e)}
      ></d-news-slide-fhi>`;
    }
    if (slide.type === 'news') {
      return html` <d-news-slide-nrk
        edit
        .slide=${slide}
        .feed=${this.newsFeed}
        @value-changed=${(e: CustomEvent<{ value: NewsSlide }>) => this.onNewsSlideChanged(e)}
      ></d-news-slide-nrk>`;
    }
    if (slide.type === 'weather') {
      return html`<d-weather-slide
        edit
        .slide=${slide}
        .assetBase=${this.assetBase}
        @value-changed=${(e: CustomEvent<{ value: WeatherSlide }>) => this.onWeatherSlideChanged(e)}
      ></d-weather-slide>`;
    }
    if (slide.type === 'employees') {
      return html`<d-employees-slide
        edit
        .slide=${slide}
        .employees=${this.employeesPresenceStatus}
        @value-changed=${(e: CustomEvent<{ value: EmployeesSlide }>) => this.onEmployeesSlideChanged(e)}
      ></d-employees-slide>`;
    }
    if (slide.type === 'image') {
      return html`<img src="${resolveAssetUrl(slide.media, this.assetBase)}" />`;
    }
    return nothing;
  }

  render() {
    return html`
      <div class="wrapper ${this.uiSize}">
        <div class="liner">
          <div class="slide main">${this.renderSlideType(this.slide)}</div>
          <div class="tools">
            ${this.uiSize === 'maxi'
              ? html`
                  <div class="tools-row first">
                    <div class="tool-section">
                      <div class="tool-label">${t('screens.editSlide.layout')}</div>
                      <div class="tools-row-content">
                        ${this.templatesWithSelectedMedia.map((t) => {
                          return html`
                            <div class="thumbnail" @click=${() => this.onSelectTemplate(t)}>
                              <d-custom-slide thumbnail .slide=${t} .assetBase=${this.assetBase}></d-custom-slide>
                            </div>
                          `;
                        })}
                      </div>
                    </div>
                  </div>
                `
              : nothing}
            <div class="split">
              <div class="left">${this.renderTools()}</div>
              <div class="right">
                <div class="actions">
                  <div><slot name="actions"></slot></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      ${this.renderSuggestionsPopover()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-edit-slide': DEditSlide;
  }
}
